import dayjs, { Dayjs } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
dayjs.extend(timezone);

class DateService {
  private readonly timezone = "Europe/Brussels";
  private readonly dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;

  public toAppDate(date: string | Date | Dayjs): Dayjs {
    const isDateOnly = typeof date === "string" && this.dateOnlyRegex.test(date);

    return isDateOnly
      ? dayjs.tz(date, "YYYY-MM-DD", this.timezone)
      : dayjs(date).tz(this.timezone);
  }

  public today(): Dayjs {
    return dayjs().tz(this.timezone).startOf("day");
  }

  public formatShortDate(date: string): string {
    return this.toAppDate(date).format("D MMM");
  }

  public formatDate(date: string, format = "DD/MM/YYYY"): string {
    return this.toAppDate(date).format(format);
  }

  public diffDays(until: string | Date | Dayjs, from: string | Date | Dayjs): number {
    return this.toAppDate(until).startOf("day").diff(this.toAppDate(from).startOf("day"), "days");
  }

  public getDatesBetween(startDate: Date | Dayjs, endDate: Date | Dayjs): string[] {
    const dates: string[] = [];

    let current = this.toAppDate(startDate).startOf("day");
    const end = this.toAppDate(endDate).startOf("day");

    while (current.isSameOrBefore(end, "day")) {
      dates.push(current.format("YYYY-MM-DD"));
      current = current.add(1, "day");
    }

    return dates;
  }

  public getDateByDayIndex(week: string[], dayIndex: number): string {
    if (!week.length) return "";

    const weekStart = this.toAppDate(week[0]).startOf("isoWeek");
    const targetDate = weekStart.add(dayIndex, "day").format("YYYY-MM-DD");

    return week.includes(targetDate) ? targetDate : "";
  }

  public getShortWeekdayLabels(): string[] {
    return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  }

  public groupDatesByIsoWeek(dates: string[]): string[][] {
    const weeks: Record<string, string[]> = {};

    dates.forEach((date: string): void => {
      const weekNumber = String(this.toAppDate(date).isoWeek());

      if (!weeks[weekNumber]) weeks[weekNumber] = [];

      weeks[weekNumber].push(date);
    });

    return Object.values(weeks);
  }
}

export default new DateService();
